const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * Returns `value` as a lowercase #rrggbb hex color, or null if it isn't #rgb or #rrggbb.
 * Node colors end up interpolated into inline `style` attributes, so anything read
 * from saved/pasted HTML must go through here to avoid CSS injection.
 */
export function normalizeHexColor(value) {
  const color = typeof value === 'string' ? value.trim() : ''
  if (!HEX_COLOR.test(color)) return null

  const hex = color.slice(1).toLowerCase()
  return hex.length === 3
    ? '#' + hex.split('').map(digit => digit + digit).join('')
    : '#' + hex
}

const toChannels = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16))

const toHex = channels => '#' + channels.map(channel => Math.round(channel).toString(16).padStart(2, '0')).join('')

/**
 * Very light tint of a #rrggbb color: mixes `amount` of it with white
 * (#045db5 → #ebf2f9, close to the badge's default #eaf1fb background).
 */
export function tintHexColor(hex, amount = 0.08) {
  return toHex(toChannels(hex).map(channel => channel * amount + 255 * (1 - amount)))
}

// WCAG 2 relative luminance and contrast ratio.
const luminance = hex => {
  const [r, g, b] = toChannels(hex).map(channel => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const contrastRatio = (a, b) => {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (light + 0.05) / (dark + 0.05)
}

/**
 * White or near-black (#111827), whichever reads better on a #rrggbb `background`.
 */
export function readableTextOn(background) {
  return contrastRatio('#ffffff', background) >= contrastRatio('#111827', background) ? '#ffffff' : '#111827'
}

/**
 * Darkens a #rrggbb color by mixing it with black in 5% steps until it reaches
 * `minRatio` contrast against `background`; returns it unchanged if it already does.
 */
export function darkenToContrast(hex, background, minRatio = 4.5) {
  let color = hex
  for (let step = 1; step <= 20 && contrastRatio(color, background) < minRatio; step++) {
    color = toHex(toChannels(hex).map(channel => channel * (1 - step / 20)))
  }
  return color
}
